// AI assisted development
/**
 * Hook for consuming feature flags in the frontend.
 * Caches flags and provides helpers to check if features are enabled.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchFeatureFlags } from '../services/adminApi';
import type { FeatureFlagsResponse } from '../types/admin';

/**
 * Known feature flag names for type safety
 */
export const FeatureFlagNames = {
  ENABLE_MULTI_PR_CONTEXT: 'ENABLE_MULTI_PR_CONTEXT',
  ENABLE_DIFF_ONLY: 'ENABLE_DIFF_ONLY',
  ENABLE_DEFECT_INVESTIGATION: 'ENABLE_DEFECT_INVESTIGATION',
  /** Functional Area catalog UI — off until quality-gated review packages exist */
  ENABLE_FUNCTIONAL_AREA_CATALOG: 'ENABLE_FUNCTIONAL_AREA_CATALOG',
  ENABLE_DEVAGENT: 'ENABLE_DEVAGENT',
  ENABLE_ISSUE_QUALITY_V2: 'ENABLE_ISSUE_QUALITY_V2',
  ENABLE_TECH_RADAR: 'ENABLE_TECH_RADAR',
  ENABLE_MEMORY: 'ENABLE_MEMORY',
  ENABLE_MEMORY_CODE: 'ENABLE_MEMORY_CODE',
  ENABLE_CODE_INDEX_STATUS: 'ENABLE_CODE_INDEX_STATUS',
  /** T030 – Pipeline navigation v2 shell */
  DEVAGENT_PIPELINES_V2: 'devagent.pipelines.v2',
} as const;

// Fail closed while the feature-flag API is loading, unavailable, or missing the flag.
export const DEVAGENT_ENABLED_BY_DEFAULT = false;

export type FeatureFlagName = keyof typeof FeatureFlagNames;

interface UseFeatureFlagsResult {
  /** Check if a specific feature flag is enabled */
  isEnabled: (flagName: string, defaultValue?: boolean) => boolean;
  /** All feature flags data */
  flags: FeatureFlagsResponse['flags'];
  /** Whether flags are still loading */
  isLoading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Refetch flags */
  refetch: () => void;
}

/**
 * Hook to access feature flags throughout the application.
 * 
 * @example
 * const { isEnabled, isLoading } = useFeatureFlags();
 * if (isEnabled('ENABLE_DEFECT_INVESTIGATION')) {
 *   // Show defect investigation feature
 * }
 */
export function useFeatureFlags(): UseFeatureFlagsResult {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<FeatureFlagsResponse>({
    queryKey: ['featureFlags'],
    queryFn: fetchFeatureFlags,
    // Cache for 5 minutes - flags don't change often
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 30 minutes
    gcTime: 30 * 60 * 1000,
    // Retry once on failure
    retry: 1,
    // Don't refetch on window focus for flags
    refetchOnWindowFocus: false,
  });

  const flags = data?.flags ?? [];

  const isEnabled = (flagName: string, defaultValue = false): boolean => {
    // While loading, return default to prevent UI flicker
    if (isLoading) {
      return defaultValue;
    }

    const flag = flags.find((f) => f.name === flagName);
    return flag?.enabled ?? defaultValue;
  };

  return {
    isEnabled,
    flags,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Simple helper to check a single flag - useful for conditional rendering
 */
export function useFeatureFlag(flagName: string, defaultValue = false): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagName, defaultValue);
}

